import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppFacade } from '../app.facade';
import { ContentRefreshHelper } from '../utils/content-refresh.helper';

@Injectable({
  providedIn: 'root',
})
export class AccountingService {
  private http = inject(HttpClient);
  readonly categories = new ContentRefreshHelper(this.getCategories())
  readonly categories$ = this.categories.data$

  readonly clients = new ContentRefreshHelper(this.getClients())
  readonly clients$ = this.clients.data$

  readonly vendors = new ContentRefreshHelper(this.getVendors())
  readonly vendors$ = this.vendors.data$

  constructor(
    private app: AppFacade,
  ) { }

  getSummary(from: Date, to: Date): Observable<any> {
    const params: any = {};

    if (from) {
      params.from = from?.toISOString ? from.toISOString() : from
    }

    if (to) {
      params.to = to?.toISOString ? to.toISOString() : to
    }

    return this.http.get(`${environment.api.carrier}v1/accounting/summary`, { params })
  }


  getCategories(): Observable<any> {
    return this.http.get(`${environment.api.carrier}v1/accounting/categories`);
  }

  updateCategory(id: string, payload: any) {
    return this.http.patch(`${environment.api.carrier}v1/accounting/categories/${id}`, payload);
  }

  deleteCategory(id: string) {
    return this.http.delete(`${environment.api.carrier}v1/accounting/categories/${id}`).pipe(
      tap({
        error: (exception) => {
          const code = exception?.error?.code;

          let message = 'An Error Occurred, Try Again Later.'

          if (code === 'accounting.category.linked') {
            // TODO: warn them if there are categories and if they still want to continue then set to uncategoroized
            message = 'There are expenses linked to this category';
          }

          this.app.showErrorModal('Error!', message);
        }
      })
    );
  }

  getClients(): Observable<any> {
    return this.http.get(`${environment.api.carrier}v1/clients`);
  }

  getVendors(): Observable<any> {
    return this.http.get(`${environment.api.carrier}v1/vendors`);
  }

  addCategory(payload: any) {
    return this.http.post(`${environment.api.carrier}v1/accounting/categories`, payload);
  }

  getAllTransactions(from?: Date, to?: Date, size?: number) {
    const params: any = {};

    if (from) {
      params.from = from?.toISOString ? from.toISOString() : from
    }

    if (to) {
      params.to = to?.toISOString ? to.toISOString() : to
    }


    if (size) {
      params.size = size
    }


    return this.http.get(`${environment.api.carrier}v1/accounting/transactions`, { params })
  }

  getTransactions(id: string) {
    return this.http.get(`${environment.api.carrier}v1/vehicles/${id}/transactions`)
  }

  getTransaction(id: string) {
    return this.http.get(`${environment.api.carrier}v1/transactions/${id}`)
  }

  getSimilarTransactions(id: string) {
    return this.http.get(`${environment.api.carrier}v1/transactions/${id}/similar`)
  }

  addTransaction(value: any): Observable<any>
  addTransaction(value: any, vehicleId: string): Observable<any>
  addTransaction(value: any, vehicleId?: any): Observable<any> {
    if (vehicleId) {
      return this.http.post(`${environment.api.carrier}v1/transactions`, value, {
        headers: {
          'x-vehicle-id': vehicleId
        }
      });
    } else if (typeof value === 'object') {
      return this.http.post(`${environment.api.carrier}v1/transactions`, value);
    }

    return throwError(() => new Error('invalid transaction parameters'))
  }

  deleteTransaction(transaction: any) {
    return this.http.delete(`${environment.api.carrier}v1/transactions/${transaction.id}`,);
  }

  updateTransaction(id: string, value: any) {
    return this.http.put(`${environment.api.carrier}v1/transactions/${id}`, value);
  }

}
